import React, { useContext, useEffect, useState } from 'react'
import { Table, Button, Dropdown, Typography, Affix, Form, Modal, App, Switch, Space, Popconfirm, Input, Upload, Avatar } from 'antd'
import { isMobileOnly, isDesktop } from "react-device-detect"
import _ from "lodash"
import XLSX from "xlsx";
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaEllipsisH, FaInbox, FaSearch, FaRegUser } from "react-icons/fa"
import { API, Enum, AntDesign, Regex } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios"

// Interfaces
import { AxiosError, AxiosResponsePaginate } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'

// Components
import CURecord from './cu-record'
import AddRole from './add-role'

const { Pages: { UserManagement: { User: UserLanguage } }, Share, Message: MessageLanguage } = language
const {  USER_MANAGEMENT_USER_PAGINATE, USER_MANAGEMENT_USER_REMOVE, USER_MANAGEMENT_USER_UPDATE, USER_MANAGEMENT_USER_CREATE_MULTI, FILE_MANAGEMENT_FILE_DOWNLOAD } = API
const { Message, UserComponent } = Enum
const { isEmail, isMobile } = Regex;
const { theme: { token: { colorPrimary } } } = AntDesign

interface FileType {
    id: string,
    mimeType: string,
    originalName: string,
    size: number,
}

interface HostType {
    id: string,
    host: string,
    title: string,
    description: string,
}

interface PermissionType {
    id: string,
    name: string,
    caption: string,
}

interface RoleType {
    id: string,
    name: string,
    caption: string,
    permissions: PermissionType[],
}

interface UserType {
    row: number,
    id: string,
    key: string,
    avatar?: FileType,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile?: string,
    email?: string,
    roles: RoleType[],
    host: HostType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

export default function Index() {

    const [, forceUpdate] = useState({})
    const [form] = Form.useForm()
    const [users, setUsers] = useState<UserType[]>([])
    const [record, setRecord] = useState<UserType|null>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [loadingRecords, setLoadingRecords] = useState<boolean>(false);
    const [sort, setSort] = useState<string|false|null|undefined>();
    const [firstName, setFirstName] = useState<string>()
    const [nationalCode, setNationalCode] = useState<string>()
    const [lastName, setLastName] = useState<string>()
    const [mobile, setMobile] = useState<string>()
    const [email, setEmail] = useState<string>()
    const [component, setComponent] = useState<string|null>(null)
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        total: undefined,
        current: 1,
        pageSize: 10,
        showTotal: isMobileOnly ? undefined : (total: number): string => `${Share.paginationShowTotal} (${total})`,
        showSizeChanger: !isMobileOnly,
        showQuickJumper: !isMobileOnly,
    });
    const { axios } = useContext(AxiosContext)
    const { baseURL, accessToken } = useContext(GlobalContext)
    const { message, modal } = App.useApp();

    const columns: ColumnsType<UserType> = [
        {
            title: Share.row,
            dataIndex: "row",
            key: "row",
        },
        {
            dataIndex: "avatar",
            key: "avatar",
            render: (avatar, record) => <Avatar size={40} icon={avatar ? <img src={baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + avatar.id + `?width=40&height=40&token=${accessToken}`} width={40} /> : <FaRegUser />} />
        },
        {
            title: UserLanguage.firstName,
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={UserLanguage.firstName}
                        onChange={({ target: { value: firstName } }) => !firstName && setFirstName(firstName)}
                        onBlur={({ target: { value: firstName } }) => setFirstName(firstName)}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={firstName && colorPrimary} />
        },
        {
            title: UserLanguage.lastName,
            dataIndex: 'lastName',
            key: 'lastName',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={UserLanguage.lastName}
                        onChange={({ target: { value: lastName } }) => !lastName && setLastName(lastName)}
                        onBlur={({ target: { value: lastName } }) => setLastName(lastName)}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={lastName && colorPrimary} />
        },
        {
            title: UserLanguage.nationalCode,
            dataIndex: 'nationalCode',
            key: 'nationalCode',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={UserLanguage.nationalCode}
                        onChange={({ target: { value: nationalCode } }) => !nationalCode && setNationalCode(nationalCode)}
                        onBlur={({ target: { value: nationalCode } }) => setNationalCode(nationalCode)}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={nationalCode && colorPrimary} />
        },
        {
            title: UserLanguage.mobile,
            dataIndex: 'mobile',
            key: 'mobile',
            sorter: true,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{padding: 8}}>
                    <Input
                        allowClear
                        placeholder={UserLanguage.mobile}
                        onChange={({ target: { value: mobile } }) => !mobile && setMobile(mobile)}
                        onBlur={({ target: { value: mobile } }) => setMobile(mobile)}
                        maxLength={11}
                    />
                </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={mobile && colorPrimary} />,
            render: (text, record) => text && <Typography.Link href={`tel:+98${text}`}>0{text}</Typography.Link>
        },
        {
            title: UserLanguage.email,
            dataIndex: 'email',
            key: 'email',
            sorter: true,
            className: 'text-align-left',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
              <div style={{padding: 8}}>
                  <Input
                    allowClear
                    placeholder={UserLanguage.email}
                    onChange={({ target: { value: email } }) => !email && setEmail(email)}
                    onBlur={({ target: { value: email } }) => setEmail(email)}
                  />
              </div>
            ),
            filterIcon: (filtered: boolean) => <FaSearch color={email && colorPrimary} />,
        },
        {
            title: UserLanguage.createdAt,
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (text, record) => text && dayjs(text).tz(process.env.REACT_APP_TZ).format('YYYY/MM/DD')
        },
        {
            title: UserLanguage.host,
            dataIndex: 'host',
            key: 'host',
            render: (text, record) => text && `${text.title} (${text.host})`
        },
        {
            title: UserLanguage.publish,
            dataIndex: 'publish',
            key: 'publish',
            render: (text, { id }) => (
                <Switch
                    checkedChildren={Share.active}
                    unCheckedChildren={Share.inactive}
                    checked={text}
                    style={{width:75}}
                    onClick={() => {
                        setLoadingRecords(true)
                        axios.patch(USER_MANAGEMENT_USER_UPDATE + id, { publish: !text }).then((response: {}) => {
                            message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                            const { current, pageSize } = pagination
                            getUsers({ page: current, limit: pageSize, sort })
                        }).catch((error: AxiosError) => {
                            const { response: { data } } = error;
                            // @ts-ignore
                            message.error(MessageLanguage[data.message]).then(response => response)
                            setLoadingRecords(false)
                        })
                    }}
                />
            )
        },
        {
            title: UserLanguage.roles,
            dataIndex: 'roles',
            key: 'roles',
            className: 'break-spaces',
            render: (text: RoleType[], record) => text.map(({ caption }) => caption).join(', ')
        },
        {
            title: Share.operation,
            dataIndex: "operation",
            key: "operation",
            render: (text, record) =>  <Dropdown menu={{ items: [
                    {
                        key: 'edit',
                        label: <Typography.Link onClick={() => {
                            setRecord(record)
                            setComponent(UserComponent.CURecord)
                            setIsModalOpen(true)
                        }}>{ Share.edit }</Typography.Link>,
                    },
                    {
                        key: 'add-role',
                        label: <Typography.Link onClick={() => {
                            setRecord(record)
                            setComponent(UserComponent.AddRole)
                            setIsModalOpen(true)
                        }}>{ UserLanguage.addRoles }</Typography.Link>,
                    },
                    {
                        key: 'delete',
                        label: <Typography.Link onClick={() => modal.confirm({
                            title: Share.deleteConfirmTitle,
                            okText: Share.deleteConfirmOk,
                            okType: 'danger',
                            cancelText: Share.deleteConfirmCancel,
                            onOk() {
                                setLoadingRecords(true)
                                axios.delete(USER_MANAGEMENT_USER_REMOVE + record.id).then((response: {}) => {
                                    const { total = 1, pageSize = 1, current = 1 } = pagination
                                    const params = {
                                        page: total % pageSize !== 1 ? current : current - 1,
                                        limit: pageSize,
                                        sort,
                                    };
                                    getUsers(params)
                                    message.success(MessageLanguage[Message.DELETE_RECORD]).then(response => response)
                                }).catch((error: AxiosError) => {
                                    const { response: { data } } = error;
                                    // @ts-ignore
                                    message.error(MessageLanguage[data.message]).then(response => response )
                                    setLoadingRecords(false)
                                })
                            }
                        })}>{ Share.delete }</Typography.Link>,
                    },
                ] }} placement="bottomRight"><a><FaEllipsisH /></a></Dropdown>
        }
    ]

    const getUsers = (params: AntTableParams) => {
        setLoadingRecords(true)
        const filters: {
            firstName?: string,
            lastName?: string,
            nationalCode?: string,
            mobile?: string,
            email?: string,
        } = {};
        if (firstName && !_.isEmpty(firstName)) filters.firstName = firstName
        if (lastName && !_.isEmpty(lastName)) filters.lastName = lastName
        if (nationalCode && !_.isEmpty(nationalCode)) filters.nationalCode = nationalCode
        if (mobile && isMobile.test(mobile)) filters.mobile = mobile.substring(1)
        if (email && isEmail.test(email)) filters.email = email
        axios.post(USER_MANAGEMENT_USER_PAGINATE, filters, { params }).then((response: AxiosResponsePaginate) => {
            const { data: { docs: users, page: current, limit: pageSize, totalDocs: total } } = response
            const { sort } = params
            let row = (current - 1) * pageSize;
            setUsers(users.map<UserType>((user: UserType) => ({ ...user, row: ++row, key: user.id })))
            setLoadingRecords(false)
            setSort(sort)
            setPagination({
                ...pagination,
                current,
                pageSize,
                total,
            })
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setLoadingRecords(false)
        })
    }

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({});
        const { pageSize } = pagination
        getUsers({ page: 1, limit: pageSize, sort })
    }, [firstName, lastName, nationalCode, mobile, email]);

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue|null>,
        sorter: SorterResult<any>|SorterResult<any>[]
    ): void => {
        const { current, pageSize } = pagination
        const sort = !_.isArray(sorter) && sorter.order && `${sorter.field},${sorter.order && sorter.order.slice(0, -3)}`
        getUsers({ page: current, limit: pageSize, sort });
    };

    const progress: any = () => ({
        strokeColor: {
            '0%': '#108ee9',
            '100%': '#87d068',
        },
        strokeWidth: 3,
        format: (percent: number) => percent && `${parseFloat(percent.toFixed())}%`,
    })

    return (
        <>
            <Modal destroyOnClose={true} title={
                (component === UserComponent.AddRole) ? `${UserLanguage.addRoles} (${record?.firstName} ${record?.lastName})` :
                    Share.addOrEditRecord
            } cancelText={Share.cancel} okText={Share.save} open={isModalOpen} onOk={() => form.submit()} onCancel={() => {
                setComponent(null)
                setIsModalOpen(false)
            }}>
                { (component === UserComponent.CURecord) && <CURecord Form={Form} form={form} record={record} getUsers={getUsers} pagination={pagination} sort={sort} /> }
                { (component === UserComponent.AddRole) && <AddRole Form={Form} form={form} record={record} getUsers={getUsers} pagination={pagination} sort={sort} /> }
            </Modal>
            <Table
                columns={columns}
                dataSource={users}
                pagination={pagination}
                loading={loadingRecords}
                onChange={handleTableChange}
                scroll={{ x: true }}
            />
            <Affix offsetBottom={30}>
                <Space.Compact block style={{marginTop:'10px'}}>
                <Button key='add' type="primary" style={{height:'40px'}} icon={<FaPlus />} onClick={() => {
                    setRecord(null)
                    setComponent(UserComponent.CURecord)
                    setIsModalOpen(true)
                }}>{ Share.add }</Button>
                    <Popconfirm
                        overlayClassName={'popconfirm-upload'}
                        icon={null}
                        title={null}
                        description={
                            <Upload.Dragger
                                action={(file) => {
                                    const { type, size } = file;
                                    if (type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" && file.type !== "application/vnd.ms-excel") {
                                        message.error(MessageLanguage.INVALID_FILE_FORMAT).then(response => response )
                                        return new Promise(() => {})
                                    }
                                    if ((size/1024/1024) > 2) {
                                        message.error(MessageLanguage.MAX_SIZE_FILE).then(response => response )
                                        return new Promise(() => {})
                                    }
                                    /* generate an array of column objects */
                                    const make_cols = (refstr: any) => {
                                        let o = [];
                                        if (refstr) {
                                            let C = XLSX.utils.decode_range(refstr).e.c + 1;
                                            for (let i = 0; i < C; ++i)
                                                o[i] = { name: XLSX.utils.encode_col(i), key: i };
                                        }
                                        return o;
                                    };
                                    return new Promise(() => {
                                        /* Boilerplate to set up FileReader */
                                        const reader = new FileReader();
                                        const rABS = !!reader.readAsBinaryString;
                                        reader.onload = (e) => {
                                            /* Parse data */
                                            // @ts-ignore
                                            const bstr = e.target.result;
                                            const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
                                            /* Get first worksheet */
                                            const wsname = wb.SheetNames[0];
                                            const ws = wb.Sheets[wsname];
                                            /* Convert array of arrays */
                                            /* data: Array of Arrays e.g. [["a","b"],[1,2]] */
                                            /* cols: Array of column objects e.g. { name: "C", K: 2 } */
                                            const data: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
                                            const cols = make_cols(ws["!ref"])
                                            const users = data.filter(mobile => mobile[0].length === 11 || mobile[0].length === 10).map(mobile => ({
                                                mobile: mobile[0].length === 11 ? mobile[0].substring(1) : mobile[0]
                                            }))
                                            setLoadingRecords(true)
                                            axios.post(USER_MANAGEMENT_USER_CREATE_MULTI, { users: _.unionBy(users, 'mobile') }).then((response: {}) => {
                                                message.success(MessageLanguage[Message.CREATE_RECORDS]).then(response => response)
                                                const { pageSize } = pagination
                                                getUsers({ page: 1, limit: pageSize, sort })
                                            }).catch((error: AxiosError) => {
                                                const { response: { data } } = error;
                                                // @ts-ignore
                                                message.error(MessageLanguage[data.message]).then(response => response)
                                                setLoadingRecords(false)
                                            })
                                        };
                                        if (rABS) reader.readAsBinaryString(file);
                                        else reader.readAsArrayBuffer(file);
                                    });
                                }}
                                multiple={false}
                                progress={progress}
                            >
                                <p className="ant-upload-drag-icon"><FaInbox size={50} /></p>
                                <p className="ant-upload-text">{ Share.attachment }</p>
                                <p className="ant-upload-hint">{ Share.attachmentDescription }</p>
                            </Upload.Dragger>
                        }
                    >
                    <Button key='multiAdd' style={{height:'40px'}} icon={<FaPlus />}>{ Share.multiAdd }</Button>
                    </Popconfirm>
                </Space.Compact>
            </Affix>
        </>
    )
}
